import { useCallback, useState } from "react";
import i18next from "i18next";
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { isImpersonationActive } from "@/hooks/impersonationState";
import { COLLECTIONS } from "@/integrations/firebase/collections";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import { ref, storage, uploadBytes } from "@/integrations/firebase/storage";
import { bugScreenshotPath, checkScreenshot } from "@/utils/attachments";

/** The rules bound each field; the form stops short of them so a person is
 *  told before they click rather than refused after it. */
export const BUG_REPORT_MAX_MESSAGE = 4000;
const MAX_PAGE_PATH = 500;
const MAX_USER_AGENT = 500;
const MAX_LANGUAGE = 20;

/** What became of a report that was sent. */
export interface ReportBugOutcome {
  /** True when a screenshot was chosen *and* stored. False both when none was
   *  chosen and when one could not be stored — the caller knows which it asked
   *  for, and the difference on screen is whether to mention it. */
  screenshotAttached: boolean;
}

export interface UseReportBugResult {
  report: (
    message: string,
    screenshot?: File | null,
  ) => Promise<ReportBugOutcome>;
  loading: boolean;
  error: Error | null;
}

/**
 * Tell the team something is wrong (PRD I3, NAV-75).
 *
 * Writes `bug_reports/{id}`, and that is the whole of the delivery: the super
 * admin's Reported bugs page reads the collection (NAV-115). There was a
 * trigger here that posted each report into a Google Chat space, and no
 * environment ever had a space — so the write was the only thing that ever
 * worked, and the form's promise of delivery was true of the document alone.
 * Now it is true of what the form says.
 *
 * **Deliberately not behind `assertWritable`**, and deliberately rather than by
 * omission. The guard exists because during an impersonation session `user` is
 * the admin while the screen belongs to somebody else, so the write would be
 * misattributed. Here it would not be: the report is the admin's own statement
 * about a screen they are looking at, `reportedById` is genuinely them, and an
 * admin checking what a user sees is one of the likelier people to find a bug.
 * What would mislead is the *context*, so that is what the report carries —
 * `impersonating` says the screen described was somebody else's.
 *
 * (`useUpdateGroupContractStatus` also writes without the guard. That one is an
 * omission rather than a decision, and is not this hook's to fix.)
 *
 * Nothing about the reporter beyond their uid is written: the Reported bugs page
 * resolves name, address, role and business unit from that account's own user document,
 * which is also why an impersonated `profile` on screen cannot leak into the
 * report.
 *
 * **A screenshot, when there is one, goes up before the document** (Jira
 * NAV-141). That order is the point: the object's location is derived from the
 * report's id and the reporter's uid rather than stored, so the document can say
 * `hasScreenshot` only once the bytes are actually there. The other order — the
 * document first, so the storage rule could resolve it — would let a report
 * claim an image a failed upload never delivered. The id is therefore minted
 * here (`doc(collection(…))`) instead of by `addDoc`.
 *
 * **What that order trades away**, said here because the paragraph above only
 * covers the direction it protects: an upload that succeeds and a `setDoc` that
 * then throws leaves bytes in Storage under a report id that never became a
 * document. Nothing lists them, a retry mints a fresh id, and the reporter
 * cannot remove them — `allow delete: if isSuperAdmin()` — so the orphan is
 * invisible and permanent until somebody clears it deliberately. Accepted rather
 * than overlooked: it costs one 5 MB object on a write that already failed in
 * front of the reporter, where the other order costs a report that lies about
 * what it carries on every write that succeeds.
 */
export function useReportBug(): UseReportBugResult {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const report = useCallback(
    async (
      message: string,
      screenshot?: File | null,
    ): Promise<ReportBugOutcome> => {
      if (!user) {
        throw new Error("Cannot report a bug without a signed-in user");
      }
      const trimmed = message.trim();
      if (!trimmed) {
        throw new Error("Cannot report a bug with an empty message");
      }

      setLoading(true);
      setError(null);
      try {
        // Minted rather than assigned by `addDoc`: the screenshot's location
        // contains this id, and it has to exist before the upload.
        const reportRef = doc(collection(db, COLLECTIONS.BUG_REPORTS));
        const screenshotAttached = screenshot
          ? await attachScreenshot(user.uid, reportRef.id, screenshot)
          : false;
        await setDoc(reportRef, {
          message: trimmed.slice(0, BUG_REPORT_MAX_MESSAGE),
          reportedById: user.uid,
          // Captured here rather than passed in, so every entry point records
          // the same thing and none of them can forget to. The href carries
          // the query string — a list's filters are often the whole bug.
          pagePath: pagePath(),
          userAgent: navigator.userAgent.slice(0, MAX_USER_AGENT),
          // What the app was rendering in — i18next's own answer rather than
          // the browser's, because the two disagree exactly when it matters:
          // a German session and an English one are different screens, and
          // "the label is cut off" is usually true in only one of them.
          language: (i18next.language || "").slice(0, MAX_LANGUAGE),
          impersonating: isImpersonationActive(),
          // Written only when true, so a report carrying nothing extra carries
          // no field either. The rules admit the key and do not require it.
          ...(screenshotAttached ? { hasScreenshot: true } : {}),
          createdAt: serverTimestamp(),
        });
        return { screenshotAttached };
      } catch (err) {
        logError(err as Error);
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user],
  );

  return { report, loading, error };
}

/**
 * Puts the screenshot where the reported-bugs page will look for it, and says
 * whether it got there.
 *
 * **Never throws, and that is the decision this function exists to make.** What
 * the reporter typed is the report; a picture is how they showed it. So a
 * refused format, a bucket that is not answering and a rule that says no all
 * come out the same way — as `false`, with the report still to be written and
 * the caller free to say that one half did not land. Throwing would lose a
 * paragraph somebody wrote because their image was 6 MB.
 *
 * The format is checked here as well as at the picker, because this is the write
 * path: the dialog's refusal is what makes the message specific and instant, and
 * this is what makes it true of every caller.
 */
async function attachScreenshot(
  uid: string,
  reportId: string,
  file: File,
): Promise<boolean> {
  const check = checkScreenshot(file);
  if (!check.ok) {
    logError(new Error(`bug-report screenshot refused: ${check.reason}`));
    return false;
  }
  try {
    // The content type comes from the extension table, never from `file.type`:
    // this is what the bytes are *served* as when the admin opens them, so it
    // has to be a value we chose (see `utils/attachments`).
    await uploadBytes(ref(storage, bugScreenshotPath(uid, reportId)), file, {
      contentType: check.contentType,
    });
    return true;
  } catch (err) {
    logError(err as Error);
    return false;
  }
}

function pagePath(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`.slice(0, MAX_PAGE_PATH);
}
